import { environment } from 'src/environments/environment';

type EnvWithMedia = { mediaBaseUrl?: string; apiUrlDomain?: string };

/**
 * Base URL for uploaded CMS images (paths like `category/…_medium.png`).
 * Prefer explicit `mediaBaseUrl` in config; else map `cms-{env}.jiomedia.net` → `img-cms-{env}.jiomedia.net`
 * (uploads are usually on the image CDN, not the API origin).
 */
export function resolveUploadedMediaBaseUrl(): string {
    const env = environment as EnvWithMedia;
    const explicit = env.mediaBaseUrl && String(env.mediaBaseUrl).trim();
    if (explicit) {
        return explicit.replace(/\/$/, '');
    }
    const api = String(env.apiUrlDomain || '');
    try {
        const u = new URL(api);
        const host = u.hostname.toLowerCase();
        const m = host.match(/^cms-([a-z0-9-]+)\.jiomedia\.net$/);
        if (m) {
            return `https://img-cms-${m[1]}.jiomedia.net`;
        }
        if (host === 'cms.jiomedia.net') {
            return 'https://img-cms.jiomedia.net';
        }
    } catch {
        /* ignore */
    }
    return api.replace(/\/api\/v1\/?$/i, '').replace(/\/$/, '');
}

/**
 * Turns API-relative media paths (e.g. `category/foo_medium.png`) into absolute URLs
 * for `<img src>`. Relative paths resolve against the current origin, which breaks on
 * `ng serve` (localhost) while uploads still go to the configured API host.
 */
export function toAbsoluteMediaUrl(path: string | null | undefined): string | null {
    if (path == null) {
        return null;
    }
    const p = String(path).trim();
    if (p === '') {
        return '';
    }
    if (p.startsWith('data:') || p.startsWith('http://') || p.startsWith('https://') || p.startsWith('//')) {
        return p;
    }
    const base = resolveUploadedMediaBaseUrl();
    if (!base) {
        return p;
    }
    const pathPart = p.startsWith('/') ? p.slice(1) : p;
    return `${base.replace(/\/$/, '')}/${pathPart}`;
}
